"use client"

import type React from "react"
import { useContext } from "react"
import { IoClose } from "react-icons/io5"
import type { EslidesEvent, Presentation } from "../../types/presentation"
import { timeFromMinutes } from "../../helpers"
import addtoschedule from "./Assets/Icons/AddSchedule.svg"
import Whiteaddtoschedule from "./Assets/Icons/WhiteAddSchedule.svg"
import WhiteRemoveschedule from "./Assets/Icons/WhiteRemoveSchedule.svg"
import removeFromSchedule from "./Assets/Icons/RemoveFromSchedule.svg"
import { useHistory } from "react-router-dom"
import EventContext from "../../contexts/event"
import quiz from "./Assets/Icons/Quiz.svg"
import forum from "./Assets/Icons/Forum.svg"
import { Calendar, Users2 } from "lucide-react"

interface ClusterProps {
  isDrawerOpen: boolean
  setIsDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>
  presentationsWithSameTime: Presentation[]
  speakerImages: { [key: string]: string }
  speakerNames: { [key: string]: string }
  scheduledPresentations: { [key: string]: boolean }
  updateScheduleStatus: (presentationId: string, eventId: string, isScheduled: boolean) => void
  totalPresentationsInPairs: number
  day: string
  rooms: { [roomId: string]: Presentation[] }
  event?: EslidesEvent
  timeGroupedPresentations: Presentation[]
  isMobileOrTablet?: boolean
}

const Cluster: React.FC<ClusterProps> = ({
  isDrawerOpen,
  setIsDrawerOpen,
  presentationsWithSameTime,
  speakerImages,
  speakerNames,
  scheduledPresentations,
  updateScheduleStatus,
  totalPresentationsInPairs,
  day,
  rooms,
  event,
  timeGroupedPresentations,
  isMobileOrTablet,
}) => {
  const history = useHistory()
  const eventContext = useContext(EventContext)


  return (
    <div
      className={`tw-fixed tw-top-0 tw-right-0 tw-h-full tw-shadow-lg tw-transform tw-transition-transform tw-duration-300 tw-overflow-auto
        tw-w-full sm:tw-w-96 md:tw-w-[28rem] lg:tw-w-[32rem]`}
      style={{
        background: "#32363F",
        color: "#D7D8D9",
        transform: isDrawerOpen ? "translateX(0)" : "translateX(100%)",
        opacity: isDrawerOpen ? 1 : 0,
        visibility: isDrawerOpen ? "visible" : "hidden",
        zIndex: 100,
      }}
    >
      {/* Close button */}
      <div className="tw-flex tw-justify-end tw-p-3 sm:tw-p-4">
        <button
          onClick={() => setIsDrawerOpen(false)}
          className="tw-text-white tw-text-xl tw-font-bold tw-bg-transparent tw-border-0 focus:tw-outline-none tw-p-2 tw-transform tw-transition-transform tw-duration-150 hover:tw-scale-110"
        >
          <IoClose />
        </button>
      </div>

      {/* Drawer content */}
      <div className="tw-px-4 sm:tw-px-6 md:tw-px-8 tw-flex tw-flex-col tw-gap-3 tw-pb-6">
        <div className={`tw-flex tw-flex-col  tw-text-sm tw-font-semibold ${isMobileOrTablet ? "tw-items-center" : "tw-items-start"}`}
          style={{ color: "#D7D8D9", fontWeight: 700 }}>
          <span>{day}</span>
          {!isMobileOrTablet ? (

            <span className="tw-whitespace-nowrap tw-text-xs sm:tw-text-sm">
              {new Date(rooms[Object.keys(rooms)[0]][0].date).toLocaleDateString("en-US", {
                weekday: "long",
                month: "short",
                day: "numeric",
                year: "numeric",
              })}
            </span>
          ) : (
            <span className="tw-whitespace-nowrap tw-text-xs sm:tw-text-sm">
              {new Date(rooms[Object.keys(rooms)[0]][0].date).toLocaleDateString("en-US", {
                weekday: "long",
                month: "short",
                day: "numeric",
                year: "numeric",
              })} - {timeFromMinutes(rooms[Object.keys(rooms)[0]][0].time)}
            </span>
          )}
        </div>
        {!isMobileOrTablet && (
          <h2 className="tw-text-sm tw-font-semibold">
            {totalPresentationsInPairs || timeGroupedPresentations.length} presentations
          </h2>
        )}
        {isMobileOrTablet && (
          <div className="tw-flex tw-flex-col tw-gap-2 sm:tw-gap-3 tw-text-xs sm:tw-text-sm tw-items-center"
            style={{ color: "#D7D8D9", fontWeight: 700 }}>
            <div className="tw-flex tw-flex-wrap tw-gap-2">
              {/* Show only the room name for the current presentation */}
              {presentationsWithSameTime && presentationsWithSameTime.length > 0 && (
                <span className="tw-bg-customColor tw-px-4 tw-py-1 tw-rounded-full">
                  {presentationsWithSameTime[0]?.room_info?.name}
                </span>
              )}
            </div>
          </div>
        )}

        <div className="tw-flex tw-flex-col lg:tw-gap-3 sm:tw-gap-0">
          {/* Show presentations with same time */}
          {presentationsWithSameTime && presentationsWithSameTime.length > 0 && (
            <>
              {presentationsWithSameTime.map((pres: any, index: number) => {
                const date = new Date(pres.date)
                const formattedDate = `${date.getDate()}/${date.toLocaleString("en-US", { month: "short" })}`

                return !isMobileOrTablet ? (
                  <div key={pres._id} className="tw-shadow-md tw-z-10 tw-flex tw-flex-col tw-bg-white tw-rounded-xl">
                    <div className="tw-p-2 sm:tw-p-3">
                      <div className="tw-flex tw-justify-between tw-items-start tw-w-full tw-gap-2 sm:tw-gap-3">
                        <div className="tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-h-full">
                          <img
                            src={speakerImages[pres._id] || "/placeholder.svg"}
                            className="tw-w-12 tw-h-12 sm:tw-w-16 sm:tw-h-16 tw-object-cover tw-object-center tw-rounded-md"
                            alt={speakerNames[pres._id] || "Speaker"}
                          />
                        </div>
                        <div className="tw-w-full tw-min-w-0">
                          <div
                            className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-text-xs tw-gap-1"
                            style={{ fontWeight: 700, color: "#4B4F5A" }}
                          >
                            <span className="tw-font-semibold tw-truncate tw-max-w-[120px] sm:tw-max-w-none">
                              {pres?.room_info?.name}
                            </span>
                            <span className="tw-whitespace-nowrap">{formattedDate}</span>
                            <span className="tw-whitespace-nowrap tw-text-[10px] sm:tw-text-xs">
                              {timeFromMinutes(pres.time)} - {timeFromMinutes(pres.end_time)}
                            </span>
                            <img
                              src={scheduledPresentations[pres._id] ? removeFromSchedule : addtoschedule}
                              alt={scheduledPresentations[pres._id] ? "Remove from Schedule" : "Add to Schedule"}
                              onClick={() =>
                                updateScheduleStatus(pres._id, pres.event_id, scheduledPresentations[pres._id])
                              }
                              className="tw-w-5 tw-h-5 sm:tw-w-6 sm:tw-h-6 tw-cursor-pointer tw-flex-shrink-0 tw-transform tw-transition-transform tw-duration-150 hover:tw-scale-11"
                            />
                          </div>
                          <h2
                            className="tw-text-base sm:tw-text-lg tw-font-bold tw-mt-1 tw-line-clamp-2"
                            style={{ color: "#4B4F5A" }}
                          >
                            {pres.name}
                          </h2>
                          <div className="tw-flex tw-flex-row tw-justify-between">
                            <p className="tw-text-xs sm:tw-text-sm tw-truncate tw-text-black">
                              {pres.speaker_info?.title} {speakerNames[pres._id] || "Speaker"}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Top box */}
                    {
                      // pres.association_id || 
                      pres.type_info && (
                        <div className="tw-shadow-sm" style={{ background: "#32363F" }}>
                          <div className="tw-flex tw-flex-row tw-p-2 tw-gap-2 sm:tw-gap-4">
                            <div className="tw-ml-1 tw-flex tw-flex-row tw-items-center tw-gap-2 tw-cursor-pointer">
                              {/* <span
                                className="tw-rounded-md tw-h-5 tw-w-5 sm:tw-h-6 sm:tw-w-6"
                                style={{ background: "#C4C4C4", border: "1px solid", borderColor: "#A5A8AF" }}
                              ></span> */}
                              <Calendar className='tw-text-white tw-h-6 tw-w-6 tw-flex tw-items-center tw-justify-center'/>
                              <span className="tw-text-white tw-text-[10px] sm:tw-text-xs tw-whitespace-nowrap">
                                {pres.type_info.name}
                              </span>
                            </div>
                            <div className="tw-flex tw-flex-row tw-items-center tw-gap-2 tw-cursor-pointer">
                              {/* <span
                                className="tw-rounded-md tw-h-6 tw-w-8 sm:tw-h-8 sm:tw-w-12"
                                style={{ background: "#C4C4C4", border: "1px solid", borderColor: "#A5A8AF" }}
                              ></span> */}
                              <Users2 className='tw-text-white tw-h-6 tw-w-6 tw-flex tw-items-center tw-justify-center'/>
                              <span className="tw-text-white tw-text-[10px] sm:tw-text-xs tw-whitespace-nowrap">
                                {pres.association_info?.name}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}

                    {/* Bottom box */}
                    {event?.forum && event.quiz && (
                      <div
                        className="tw-shadow-sm"
                        style={{
                          background: "#1080B2",
                          borderBottomRightRadius: "0.625rem",
                          borderBottomLeftRadius: "0.625rem",
                        }}
                      >
                        <div className="tw-flex tw-flex-row tw-p-2 tw-gap-4 sm:tw-gap-8">
                          <div
                            className="tw-ml-1 tw-flex tw-flex-row tw-items-center tw-gap-1 tw-cursor-pointer tw-transform tw-transition-transform tw-duration-150 hover:tw-scale-110"
                            onClick={() => {
                              history.push(`/${eventContext.slug}/${pres._id}/forum${document.location.search}`)
                            }}
                          >
                            <img
                              src={quiz || "/placeholder.svg"}
                              alt="Quiz"
                              className="tw-w-4 tw-h-4 sm:tw-w-5 sm:tw-h-5"
                            />
                            <span className="tw-font-semibold tw-text-white tw-text-xs sm:tw-text-md">FORUM</span>
                          </div>
                          <div
                            className="tw-flex tw-flex-row tw-items-center tw-gap-1 tw-cursor-pointer tw-transform tw-transition-transform tw-duration-150 hover:tw-scale-110"
                            onClick={() => {
                              history.push(
                                `/${eventContext.slug}/${pres._id}/quiz-moderator${document.location.search}`,
                              )
                            }}
                          >
                            <img
                              src={forum || "/placeholder.svg"}
                              alt="Forum"
                              className="tw-w-4 tw-h-4 sm:tw-w-5 sm:tw-h-5"
                            />
                            <span className="tw-font-semibold tw-text-white tw-text-xs sm:tw-text-sm">QUIZ</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <div key={pres._id} className="tw-shadow-sm tw-z-10 tw-flex tw-flex-col tw-bg-white lg:tw-rounded-md tw-rounded-t-md tw-mt-14">
                      <div className="tw-px-4 sm:tw-px-6 tw-py-3">
                        <div className="tw-flex tw-justify-between tw-items-center tw-w-full">
                          <div className="tw-w-full">
                            <div
                              className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-text-xs sm:tw-text-sm tw-gap-1"
                              style={{ fontWeight: 700, color: "#4B4F5A" }}
                            >
                              <span className="tw-font-semibold tw-truncate tw-max-w-[100px] sm:tw-max-w-none">
                                {pres?.room_info?.name}
                              </span>
                              <span className="tw-whitespace-nowrap">{formattedDate}</span>
                              <span className="tw-whitespace-nowrap tw-text-[10px] sm:tw-text-xs">
                                {timeFromMinutes(pres.time)} - {timeFromMinutes(pres.end_time)}
                              </span>
                            </div>
                            <div className="tw-flex tw-items-center tw-flex-shrink-0 tw-h-full tw-gap-2 sm:tw-gap-2.5 tw-mt-3">
                              <img
                                src={speakerImages[pres._id] || "/placeholder.svg"}
                                className="tw-w-12 tw-h-12 sm:tw-w-16 sm:tw-h-16 tw-object-cover tw-object-center tw-rounded-md tw-flex-shrink-0"
                                alt={speakerNames[pres._id] || "Speaker"}
                              />
                              <div className="tw-w-full tw-min-w-0">
                                <h2
                                  className="tw-text-base sm:tw-text-lg tw-font-bold tw-line-clamp-2"
                                  style={{ color: "#4B4F5A" }}
                                >
                                  {pres.name}
                                </h2>
                                <span className="tw-text-xs sm:tw-text-sm tw-truncate tw-text-black tw-block">
                                  {pres.speaker_info?.title} {speakerNames[pres._id] || "Speaker"}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Bottom box */}
                    {event?.forum && event.quiz && (
                      <div className="tw-shadow-sm tw-rounded-b-md" style={{ background: "#4B4F5A" }}>
                        <div className="tw-flex tw-flex-col tw-gap-4 sm:tw-gap-6 tw-px-4 sm:tw-px-6 tw-py-4 sm:tw-py-6">
                          {/* FORUM */}
                          <div
                            className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer tw-transform tw-transition-transform tw-duration-150 hover:tw-scale-110"
                            onClick={() => {
                              history.push(`/${eventContext.slug}/${pres._id}/forum${document.location.search}`)
                            }}
                          >
                            <div className="tw-w-5 sm:tw-w-6 tw-flex-shrink-0">
                              <img src={quiz || "/placeholder.svg"} alt="Quiz" className="tw-w-full" />
                            </div>
                            <span className="tw-font-semibold tw-text-white tw-text-sm sm:tw-text-md">FORUM</span>
                          </div>
                          {/* QUIZ */}
                          <div
                            className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer tw-transform tw-transition-transform tw-duration-150 hover:tw-scale-110"
                            onClick={() => {
                              history.push(
                                `/${eventContext.slug}/${pres._id}/quiz-moderator${document.location.search}`,
                              )
                            }}
                          >
                            <div className="tw-w-5 sm:tw-w-6 tw-flex-shrink-0">
                              <img src={forum || "/placeholder.svg"} alt="Forum" className="tw-w-full" />
                            </div>
                            <span className="tw-font-semibold tw-text-white tw-text-sm">QUIZ</span>
                          </div>
                        </div>
                      </div>
                    )}
                    {!scheduledPresentations[pres._id] ? (
                      <button
                        className="tw-flex tw-items-center tw-justify-center tw-gap-2 sm:tw-gap-3 tw-font-montserrat tw-text-white tw-border-none tw-p-3 sm:tw-p-3 tw-text-sm sm:tw-text-base lg:tw-mt-0 tw-mt-3 focus:tw-outline-none tw-transform tw-transition-transform tw-duration-150 hover:tw-scale-110"
                        style={{
                          fontWeight: 700,
                          background: "#24B2FF",
                          borderRadius: "5px",
                        }}
                        onClick={() => updateScheduleStatus(pres._id, pres.event_id, scheduledPresentations[pres._id])}
                      >
                        <img
                          src={Whiteaddtoschedule}
                          alt={"Add to Schedule"}
                          className="tw-w-5 tw-h-5 sm:tw-w-6 sm:tw-h-6 tw-cursor-pointer"
                        />
                        ADD TO MY CALENDER
                      </button>
                    ) : (
                      <button
                        className="tw-flex tw-items-center tw-justify-center tw-gap-2 sm:tw-gap-3 tw-font-montserrat tw-text-white tw-border-none tw-p-3 sm:tw-p-3 tw-text-sm sm:tw-text-base lg:tw-mt-0 tw-mt-3 focus:tw-outline-none tw-transform tw-transition-transform tw-duration-150 hover:tw-scale-110"
                        style={{
                          fontWeight: 700,
                          background: "#24B2FF",
                          borderRadius: "5px",
                        }}
                        onClick={() => updateScheduleStatus(pres._id, pres.event_id, scheduledPresentations[pres._id])}
                      >
                        <img
                          src={WhiteRemoveschedule}
                          alt={"Remove from Schedule"}
                          className="tw-w-5 tw-h-5 sm:tw-w-6 sm:tw-h-6 tw-cursor-pointer"
                        />
                        REMOVE FROM MY CALENDER
                      </button>
                    )}
                  </>
                )
              })}
            </>
          )}

          {/* Show time grouped presentations */}
          {timeGroupedPresentations && timeGroupedPresentations.length > 0 && (
            <>
              {timeGroupedPresentations.map((pres: any, index: number) => {
                const date = new Date(pres.date)
                const formattedDate = `${date.getDate()}/${date.toLocaleString("en-US", { month: "short" })}`

                return (
                  <div key={pres._id} className="tw-shadow-md tw-z-10 tw-flex tw-flex-col tw-bg-white tw-rounded-xl">
                    <div className="tw-p-2 sm:tw-p-3">
                      <div className="tw-flex tw-justify-between tw-items-start tw-w-full tw-gap-2 sm:tw-gap-3">
                        <div className="tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-h-full">
                          <img
                            src={speakerImages[pres._id] || "/placeholder.svg"}
                            className="tw-w-12 tw-h-12 sm:tw-w-16 sm:tw-h-16 tw-object-cover tw-object-center tw-rounded-md"
                            alt={speakerNames[pres._id] || "Speaker"}
                          />
                        </div>
                        <div className="tw-w-full tw-min-w-0">
                          <div
                            className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-text-xs tw-gap-1"
                            style={{ fontWeight: 700, color: "#4B4F5A" }}
                          >
                            <span className="tw-font-semibold tw-truncate tw-max-w-[120px] sm:tw-max-w-none">
                              {pres?.room_info?.name}
                            </span>
                            <span className="tw-whitespace-nowrap">{formattedDate}</span>
                            <span className="tw-whitespace-nowrap tw-text-[10px] sm:tw-text-xs">
                              {timeFromMinutes(pres.time)} - {timeFromMinutes(pres.end_time)}
                            </span>
                            <img
                              src={scheduledPresentations[pres._id] ? removeFromSchedule : addtoschedule}
                              alt={scheduledPresentations[pres._id] ? "Remove from Schedule" : "Add to Schedule"}
                              onClick={() =>
                                updateScheduleStatus(pres._id, pres.event_id, scheduledPresentations[pres._id])
                              }
                              className="tw-w-5 tw-h-5 sm:tw-w-6 sm:tw-h-6 tw-cursor-pointer tw-flex-shrink-0 tw-transform tw-transition-transform tw-duration-150 hover:tw-scale-110"
                            />
                          </div>
                          <h2
                            className="tw-text-base sm:tw-text-lg tw-font-bold tw-mt-1 tw-line-clamp-2"
                            style={{ color: "#4B4F5A" }}
                          >
                            {pres.name}
                          </h2>
                          <div className="tw-flex tw-flex-row tw-justify-between">
                            <p className="tw-text-xs sm:tw-text-sm tw-truncate tw-text-black">
                              {pres.speaker_info?.title} {speakerNames[pres._id] || "Speaker"}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Top box - hidden on mobile */}
                    {!isMobileOrTablet && pres.type_info?.name && pres.association_info?.name && (
                      <div className="tw-shadow-sm" style={{ background: "#32363F" }}>
                        <div className="tw-flex tw-flex-row tw-p-2 tw-gap-2 sm:tw-gap-4">
                          <div className="tw-ml-1 tw-flex tw-flex-row tw-items-center tw-gap-2 tw-cursor-pointer">
                            {/* <span
                              className="tw-rounded-md tw-h-5 tw-w-5 sm:tw-h-6 sm:tw-w-6"
                              style={{ background: "#C4C4C4", border: "1px solid", borderColor: "#A5A8AF" }}
                            ></span> */}
                            <Calendar className='tw-text-white tw-h-6 tw-w-6 tw-flex tw-items-center tw-justify-center'/>
                            <span className="tw-text-white tw-text-[10px] sm:tw-text-xs tw-whitespace-nowrap">
                              {pres.type_info.name}
                            </span>
                          </div>
                          <div className="tw-flex tw-flex-row tw-items-center tw-gap-2 tw-cursor-pointer">
                            {/* <span
                              className="tw-rounded-md tw-h-6 tw-w-8 sm:tw-h-8 sm:tw-w-12"
                              style={{ background: "#C4C4C4", border: "1px solid", borderColor: "#A5A8AF" }}
                            ></span> */}                                                    
                            <Users2 className='tw-text-white tw-h-6 tw-w-6 tw-flex tw-items-center tw-justify-center'/>
                            <span className="tw-text-white tw-text-[10px] sm:tw-text-xs tw-whitespace-nowrap">
                              {pres.association_info?.name}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Bottom box */}
                    {event?.forum && event.quiz && (
                      <div
                        className="tw-shadow-sm"
                        style={{
                          background: "#1080B2",
                          borderBottomRightRadius: "0.625rem",
                          borderBottomLeftRadius: "0.625rem",
                        }}
                      >
                        <div className="tw-flex tw-flex-row tw-p-2 tw-gap-4 sm:tw-gap-8">
                          <div
                            className="tw-ml-1 tw-flex tw-flex-row tw-items-center tw-gap-1 tw-cursor-pointer tw-transform tw-transition-transform tw-duration-150 hover:tw-scale-110"
                            onClick={() => {
                              history.push(`/${eventContext.slug}/${pres._id}/forum${document.location.search}`)
                            }}
                          >
                            <img
                              src={quiz || "/placeholder.svg"}
                              alt="Quiz"
                              className="tw-w-4 tw-h-4 sm:tw-w-5 sm:tw-h-5"
                            />
                            <span className="tw-font-semibold tw-text-white tw-text-xs sm:tw-text-md">FORUM</span>
                          </div>
                          <div
                            className="tw-flex tw-flex-row tw-items-center tw-gap-1 tw-cursor-pointer tw-transform tw-transition-transform tw-duration-150 hover:tw-scale-110"
                            onClick={() => {
                              history.push(
                                `/${eventContext.slug}/${pres._id}/quiz-moderator${document.location.search}`,
                              )
                            }}
                          >
                            <img
                              src={forum || "/placeholder.svg"}
                              alt="Forum"
                              className="tw-w-4 tw-h-4 sm:tw-w-5 sm:tw-h-5"
                            />
                            <span className="tw-font-semibold tw-text-white tw-text-xs sm:tw-text-sm">QUIZ</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default Cluster
